<!--
  Preview toolbar (Issue #2542): three menus grouped by intent. The closed triggers show the zoom
  percentage, a scale badge and the active tool, so the common state is visible without opening
  anything. Every command keeps the test ID it had as a standalone button.
-->
<div
  class="pdf-toolbar"
  data-testid="preview-toolbar"
  role="toolbar"
  aria-label="Preview toolbar"
  [class.visible]="internalVisible"
>
  <div class="toolbar-content">
    <!-- ── Zoom ─────────────────────────────────────────────── -->
    <!--
      Zoom opens a small non-modal dialog, not a menu (QA on Issue #2542). It holds a text field,
      and a menu may only contain menu items, so screen readers could not reach the field properly
      inside one. It looks like the other two menus and closes the same ways: Escape, a click
      outside, or choosing a fit.
    -->
    <button
      mat-button
      type="button"
      class="group-trigger zoom-trigger"
      data-testid="preview-zoom-menu"
      cdkOverlayOrigin
      #zoomOrigin="cdkOverlayOrigin"
      #zoomTrigger
      aria-haspopup="dialog"
      [attr.aria-expanded]="zoomOpen ? 'true' : 'false'"
      [attr.aria-controls]="zoomOpen ? 'preview-zoom-panel' : null"
      [attr.aria-label]="'Zoom, ' + getScalePercentage()"
      (click)="toggleZoomPanel($event)"
      matTooltip="Zoom"
      [matTooltipDisabled]="triggerTooltipsOff"
      matTooltipPosition="below"
    >
      <mat-icon aria-hidden="true">zoom_in</mat-icon>
      <span class="trigger-text zoom-level" data-testid="preview-zoom-level">{{ getScalePercentage() }}</span>
      <mat-icon iconPositionEnd aria-hidden="true" class="trigger-caret">arrow_drop_down</mat-icon>
    </button>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="zoomOrigin"
      [cdkConnectedOverlayOpen]="zoomOpen"
      [cdkConnectedOverlayPositions]="zoomPanelPositions"
      [cdkConnectedOverlayPush]="true"
      [cdkConnectedOverlayViewportMargin]="8"
      [cdkConnectedOverlayHasBackdrop]="true"
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      (backdropClick)="closeZoomPanel()"
      (overlayKeydown)="onZoomPanelKeydown($event)"
      (detach)="closeZoomPanel()"
      (attach)="onZoomPanelAttached()"
    >
      <div
        #zoomPanel
        id="preview-zoom-panel"
        class="mat-mdc-menu-panel preview-toolbar-menu zoom-panel"
        role="dialog"
        aria-label="Zoom"
        tabindex="-1"
        cdkTrapFocus
      >
        <div class="mat-mdc-menu-content">
          <!--
            The − and + buttons have no tooltip: an open tooltip takes the first Escape for itself,
            and one Escape must close the panel. Their names are announced and the shortcuts shown.
          -->
          <label class="zoom-caption" for="preview-zoom-input" data-testid="preview-zoom-set">
            <span class="menu-label">Adjust zoom level</span>
            <kbd class="menu-shortcut" aria-hidden="true">+ / −</kbd>
          </label>
          <div class="zoom-entry" role="group" aria-label="Zoom level">
            <button
              mat-icon-button
              type="button"
              [attr.data-testid]="cmd('zoomOut').testId"
              [attr.aria-label]="cmd('zoomOut').label"
              aria-keyshortcuts="-"
              (click)="onMenuCommand(cmd('zoomOut'), $event)"
            >
              <mat-icon>remove</mat-icon>
            </button>
            <input
              #zoomInput
              id="preview-zoom-input"
              type="text"
              inputmode="numeric"
              class="zoom-input"
              data-testid="preview-zoom-input"
              aria-label="Zoom percentage, 20 to 500"
              [(ngModel)]="zoomInputValue"
              (focus)="onZoomInputFocus()"
              (blur)="onZoomInputBlur()"
              (keydown)="onZoomInputKeyDown($event)"
              autocomplete="off"
            />
            <button
              mat-icon-button
              type="button"
              [attr.data-testid]="cmd('zoomIn').testId"
              [attr.aria-label]="cmd('zoomIn').label"
              aria-keyshortcuts="+"
              (click)="onMenuCommand(cmd('zoomIn'), $event)"
            >
              <mat-icon>add</mat-icon>
            </button>
          </div>
          <mat-divider></mat-divider>
          <!--
            The three fit modes are one radio group: the one the page is currently fitted to is
            checked, and none is checked once the reviewer has zoomed away from it. Arrow keys move
            between them; choosing one fits the page and closes the panel.
          -->
          <div #fitGroup role="radiogroup" aria-label="Fit page">
            @for (fit of fitCommands; track fit.id; let index = $index) {
              <button
                type="button"
                class="mat-mdc-menu-item zoom-fit"
                role="radio"
                [attr.data-testid]="fit.testId"
                [attr.aria-checked]="selectedFit === fit.mode ? 'true' : 'false'"
                [attr.tabindex]="fitTabIndex(index)"
                [attr.aria-keyshortcuts]="fit.shortcut"
                [class.active]="selectedFit === fit.mode"
                (click)="onFitChosen(fit)"
                (keydown)="onFitKeydown($event, fitGroup)"
              >
                <span class="mat-mdc-menu-item-text">
                  <mat-icon [fontSet]="fit.fontSet || ''" aria-hidden="true">{{ fit.icon }}</mat-icon>
                  <span class="menu-label">{{ fit.label }}</span>
                  @if (fit.shortcut) {
                    <kbd class="menu-shortcut" aria-hidden="true">{{ fit.shortcut }}</kbd>
                  }
                  @if (selectedFit === fit.mode) {
                    <span class="menu-check material-icons" aria-hidden="true">check</span>
                  }
                </span>
              </button>
            }
          </div>
        </div>
      </div>
    </ng-template>

    <!-- ── Page ─────────────────────────────────────────────── -->
    <button
      mat-button
      type="button"
      class="group-trigger page-trigger"
      data-testid="preview-page-menu"
      [attr.data-scale-status]="scaleStatus"
      [attr.aria-label]="pageAriaLabel"
      [attr.aria-busy]="isRotating ? 'true' : null"
      [matMenuTriggerFor]="pageMenu"
      (menuOpened)="onMenuOpened()"
      (menuClosed)="onMenuClosed()"
      [matTooltip]="settingsTooltip"
      [matTooltipDisabled]="triggerTooltipsOff"
      matTooltipPosition="below"
    >
      <!--
        The gear is a direct child, so Material centres it like the Tools icon. Inside a wrapper it
        went into the button's text slot and sat 3 px high (requester's feedback on #2542).
      -->
      <mat-icon aria-hidden="true">{{ isRotating ? 'hourglass_top' : 'settings' }}</mat-icon>
      @if (showScaleBadge) {
        <span
          class="scale-badge"
          data-testid="preview-page-scale-badge"
          [class.invalidated]="scaleStatus === 'invalidated'"
          [class.none]="scaleStatus === 'none'"
          aria-hidden="true"
        ></span>
      }
      <!-- A small caret marks the button as a menu (requester's feedback on #2542). -->
      <mat-icon iconPositionEnd aria-hidden="true" class="trigger-caret">arrow_drop_down</mat-icon>
    </button>
    <mat-menu #pageMenu="matMenu" class="preview-toolbar-menu" xPosition="before">
      <button
        mat-menu-item
        type="button"
        [attr.data-testid]="cmd('rotateLeft').testId"
        data-cuj-effect="persistent-page-mutation"
        [attr.aria-label]="cmd('rotateLeft').label + ', 90° counter-clockwise'"
        [attr.aria-keyshortcuts]="cmd('rotateLeft').shortcut"
        [disabled]="isRotating || canEdit === false"
        (click)="onMenuCommand(cmd('rotateLeft'))"
      >
        @if (isRotating) {
          <mat-progress-spinner
            class="menu-spinner"
            mode="indeterminate"
            diameter="20"
            aria-hidden="true"
          ></mat-progress-spinner>
        } @else {
          <mat-icon>{{ cmd('rotateLeft').icon }}</mat-icon>
        }
        <span class="menu-label">{{ isRotating ? 'Rotating…' : 'Rotate left' }}</span>
        <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('rotateLeft').shortcut }}</kbd>
      </button>
      <button
        mat-menu-item
        type="button"
        [attr.data-testid]="cmd('rotateRight').testId"
        data-cuj-effect="persistent-page-mutation"
        [attr.aria-label]="cmd('rotateRight').label + ', 90° clockwise'"
        [attr.aria-keyshortcuts]="cmd('rotateRight').shortcut"
        [disabled]="isRotating || canEdit === false"
        (click)="onMenuCommand(cmd('rotateRight'))"
      >
        @if (isRotating) {
          <mat-progress-spinner
            class="menu-spinner"
            mode="indeterminate"
            diameter="20"
            aria-hidden="true"
          ></mat-progress-spinner>
        } @else {
          <mat-icon>{{ cmd('rotateRight').icon }}</mat-icon>
        }
        <span class="menu-label">{{ isRotating ? 'Rotating…' : 'Rotate right' }}</span>
        <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('rotateRight').shortcut }}</kbd>
      </button>
      <mat-divider></mat-divider>

      <!--
        Scale section (Issue #2442): a status header that arrow keys skip (it is not a menu item),
        then the actions that set, inspect or remove the page scale.
      -->
      <div class="menu-section" role="group" aria-labelledby="preview-scale-status-label">
        <div
          class="menu-status"
          id="preview-scale-status-label"
          data-testid="preview-scale-status"
          [attr.data-scale-status]="scaleStatus"
          [class.invalidated]="scaleStatus === 'invalidated'"
          [class.comparing]="comparingScale"
        >
          <mat-icon aria-hidden="true">{{ scaleStatusIcon }}</mat-icon>
          <span class="menu-status-text" data-testid="preview-scale-indicator">{{ scaleLine }}</span>
        </div>
        <button
          mat-menu-item
          type="button"
          [attr.data-testid]="cmd('calibrateScale').testId"
          [attr.data-scale-status]="scaleStatus"
          [attr.aria-label]="calibrateMenuLabel"
          [attr.aria-keyshortcuts]="cmd('calibrateScale').shortcut"
          [disabled]="canEdit === false"
          (click)="onMenuCommand(cmd('calibrateScale'))"
        >
          <mat-icon>{{ calibrating ? 'close' : cmd('calibrateScale').icon }}</mat-icon>
          <span class="menu-label">{{ calibrateMenuLabel }}</span>
          <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('calibrateScale').shortcut }}</kbd>
        </button>
        @if (scaleStatus === 'manual' || scaleStatus === 'invalidated') {
          <button
            mat-menu-item
            type="button"
            [attr.data-testid]="cmd('showScaleDetails').testId"
            (click)="onMenuCommand(cmd('showScaleDetails'))"
          >
            <mat-icon>{{ cmd('showScaleDetails').icon }}</mat-icon>
            <span class="menu-label">{{ cmd('showScaleDetails').label }}</span>
          </button>
        }
        @if (scaleStatus === 'manual') {
          <button
            mat-menu-item
            type="button"
            role="menuitemcheckbox"
            [attr.data-testid]="cmd('toggleScaleCompare').testId"
            [attr.aria-checked]="comparingScale ? 'true' : 'false'"
            (click)="onMenuCommand(cmd('toggleScaleCompare'))"
          >
            <mat-icon>{{ comparingScale ? 'check_box' : 'check_box_outline_blank' }}</mat-icon>
            <span class="menu-label">{{ cmd('toggleScaleCompare').label }}</span>
          </button>
        }
        @if (scaleStatus === 'manual' || scaleStatus === 'invalidated') {
          <button
            mat-menu-item
            type="button"
            [attr.data-testid]="cmd('resetScale').testId"
            data-cuj-effect="persistent-page-mutation"
            [disabled]="canEdit === false"
            (click)="onMenuCommand(cmd('resetScale'))"
          >
            <mat-icon>{{ cmd('resetScale').icon }}</mat-icon>
            <span class="menu-label">{{ cmd('resetScale').label }}</span>
          </button>
        }
      </div>
      <mat-divider></mat-divider>
      <button
        mat-menu-item
        type="button"
        [attr.data-testid]="cmd('openInNewTab').testId"
        [attr.aria-label]="cmd('openInNewTab').label"
        (click)="onMenuCommand(cmd('openInNewTab'))"
      >
        <mat-icon>{{ cmd('openInNewTab').icon }}</mat-icon>
        <span class="menu-label">{{ cmd('openInNewTab').label }}</span>
      </button>
    </mat-menu>

    <!-- ── Tools ────────────────────────────────────────────── -->
    <button
      mat-button
      type="button"
      class="group-trigger tools-trigger"
      data-testid="preview-tools-menu"
      [class.active]="!!activeTool"
      [attr.data-active-tool]="activeTool"
      [attr.aria-label]="toolsAriaLabel"
      [matMenuTriggerFor]="toolsMenu"
      (menuOpened)="onMenuOpened()"
      (menuClosed)="onMenuClosed()"
      matTooltip="Tools"
      [matTooltipDisabled]="triggerTooltipsOff"
      matTooltipPosition="below"
    >
      <mat-icon aria-hidden="true">{{ toolsIcon }}</mat-icon>
      <mat-icon iconPositionEnd aria-hidden="true" class="trigger-caret">arrow_drop_down</mat-icon>
    </button>
    <!--
      The exit button has no tooltip, and the triggers show none while a tool is active or just after
      a menu closed: a visible tooltip takes the next Escape for itself, and that Escape belongs to
      the tool. The button's name ("Exit Ruler") is announced, and Exit tool in the menu lists Esc.
    -->
    <!-- The exit button takes space only while a tool is active; no empty slot is kept for it. -->
    @if (activeTool) {
      <span class="tool-exit-slot">
        <button
          mat-icon-button
          type="button"
          class="tool-exit"
          data-testid="preview-tool-exit"
          [attr.aria-label]="'Exit ' + toolsLabel"
          (click)="onAction('exitTool')"
        >
          <mat-icon>close</mat-icon>
        </button>
      </span>
    }
    <mat-menu #toolsMenu="matMenu" class="preview-toolbar-menu" xPosition="before">
      <button
        mat-menu-item
        type="button"
        role="menuitemradio"
        [attr.data-testid]="cmd('toggleRuler').testId"
        [attr.aria-label]="cmd('toggleRuler').label"
        [attr.aria-keyshortcuts]="cmd('toggleRuler').shortcut"
        [attr.aria-checked]="activeTool === 'ruler' ? 'true' : 'false'"
        [class.active]="activeTool === 'ruler'"
        (click)="onMenuCommand(cmd('toggleRuler'))"
        [matTooltip]="toolHints.ruler"
        matTooltipPosition="left"
        (keydown.escape)="toolsMenu.closed.emit('keydown')"
      >
        <mat-icon>{{ cmd('toggleRuler').icon }}</mat-icon>
        <span class="menu-label">
          {{ cmd('toggleRuler').label }}
        </span>
        <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('toggleRuler').shortcut }}</kbd>
        @if (activeTool === 'ruler') {
          <span class="menu-check material-icons" aria-hidden="true">check</span>
        }
      </button>
      <button
        mat-menu-item
        type="button"
        role="menuitemradio"
        [attr.data-testid]="cmd('toggleArea').testId"
        [attr.aria-label]="cmd('toggleArea').label"
        [attr.aria-keyshortcuts]="cmd('toggleArea').shortcut"
        [attr.aria-checked]="activeTool === 'area' ? 'true' : 'false'"
        [class.active]="activeTool === 'area'"
        (click)="onMenuCommand(cmd('toggleArea'))"
        [matTooltip]="toolHints.area"
        matTooltipPosition="left"
        (keydown.escape)="toolsMenu.closed.emit('keydown')"
      >
        <mat-icon>{{ cmd('toggleArea').icon }}</mat-icon>
        <span class="menu-label">
          {{ cmd('toggleArea').label }}
        </span>
        <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('toggleArea').shortcut }}</kbd>
        @if (activeTool === 'area') {
          <span class="menu-check material-icons" aria-hidden="true">check</span>
        }
      </button>
      <mat-divider></mat-divider>
      <!--
        What each tool is for is a tooltip, not a second line of text in the menu (requester's
        feedback on #2542). A Material tooltip shows on hover, on keyboard focus and on a long press,
        and is read out as the item's description. A visible tooltip would take Escape for itself, so
        each item with one closes the menu on Escape first: one press still closes the menu.
      -->
      <!--
        Select area shapes (Issues #2439, #2440, #1747 and #2542). Choosing a shape starts Select
        area with it, so each shape is two interactions away and no shape needs a toolbar button.
      -->
      <div class="menu-section" role="group" aria-labelledby="preview-area-shapes-label">
        <div class="menu-heading" id="preview-area-shapes-label">Select area shape</div>
        @for (option of areaShapes; track option.shape) {
          <button
            mat-menu-item
            type="button"
            role="menuitemradio"
            [attr.data-testid]="option.testId"
            [attr.aria-checked]="activeAreaShape === option.shape ? 'true' : 'false'"
            [class.active]="activeAreaShape === option.shape"
            (click)="onAreaShape(option.shape)"
            (keydown.escape)="toolsMenu.closed.emit('keydown')"
          >
            <mat-icon>{{ option.icon }}</mat-icon>
            <span class="menu-label">
              {{ option.label }}
            </span>
            @if (activeAreaShape === option.shape) {
              <span class="menu-check material-icons" aria-hidden="true">check</span>
            }
          </button>
        }
      </div>
      <mat-divider></mat-divider>
      <!-- Not a shape: it picks up and moves areas that were already drawn. -->
      <button
        mat-menu-item
        type="button"
        role="menuitemcheckbox"
        [attr.data-testid]="selectAndMove.testId"
        [attr.aria-keyshortcuts]="selectAndMove.shortcut"
        [attr.aria-checked]="activeAreaShape === 'select' ? 'true' : 'false'"
        [class.active]="activeAreaShape === 'select'"
        (click)="onAreaShape('select')"
        [matTooltip]="toolHints.selectAndMove"
        matTooltipPosition="left"
        (keydown.escape)="toolsMenu.closed.emit('keydown')"
      >
        <mat-icon>{{ selectAndMove.icon }}</mat-icon>
        <span class="menu-label"> Select &amp; move areas </span>
        <kbd class="menu-shortcut" aria-hidden="true">{{ selectAndMove.shortcut }}</kbd>
        @if (activeAreaShape === 'select') {
          <span class="menu-check material-icons" aria-hidden="true">check</span>
        }
      </button>
      @if (activeTool) {
        <mat-divider></mat-divider>
        <button
          mat-menu-item
          type="button"
          [attr.data-testid]="cmd('exitTool').testId"
          aria-keyshortcuts="Escape"
          (click)="onMenuCommand(cmd('exitTool'))"
        >
          <mat-icon>{{ cmd('exitTool').icon }}</mat-icon>
          <span class="menu-label">{{ cmd('exitTool').label }}</span>
          <kbd class="menu-shortcut" aria-hidden="true">{{ cmd('exitTool').shortcut }}</kbd>
        </button>
      }
    </mat-menu>
  </div>
</div>
